import * as Tone from 'tone';
import { clamp, frequencyOf, toneName } from '../config/music';
import type { Settings } from '../state/settings';
import {
  ensureGraph,
  setActiveVoiceCount,
  setEchoWet,
  setFilterFrequency,
  setReverbWet,
  setSourceTrim,
  setVolume,
} from './graph';
import { rewire } from './effects-chain';
import { buildSynthSource, currentSource, disposeSource, sourceTrimFor, updateSourceOptions } from './synth-source';
import type { BuildResult } from './synth-source';
import { currentSampler, detachSampler, ensureSampler, isSamplerReady, setSamplerEnvelope } from './sampler';

/** Reference pitch for the register curve: middle C. */
const REFERENCE_FREQ = 261.63;
const REGISTER_MAX_BOOST_DB = 12;
const REGISTER_MAX_CUT_DB = -18;

/**
 * Base velocity for synth engines, chosen so the loudest the register curve can
 * ask for (+12 dB, i.e. x4) still lands exactly on 1.0. Velocity is an envelope
 * peak and Tone expects it inside 0..1; the previous code computed values up to
 * 2.5, which clipped, and saturated at the clamp so the bass slider had no
 * audible effect across most of its range.
 */
const SYNTH_BASE_VELOCITY = 0.25;
/** Flat velocity for samples when the register curve is not applied to them. */
const SAMPLE_FLAT_VELOCITY = 0.85;

/** dB of level change applied at this pitch, relative to middle C. */
export function registerCurveDb(abs: number, s: Settings): number {
  if (s.engine === 'sampler' && !s.registerCurveOnSamples) return 0;
  const octaves = Math.log2(frequencyOf(abs) / REFERENCE_FREQ);
  const db = octaves < 0 ? -octaves * s.bassCurveDb : -octaves * s.trebleCurveDb;
  return clamp(db, REGISTER_MAX_CUT_DB, REGISTER_MAX_BOOST_DB);
}

export const registerCurveGain = (abs: number, s: Settings): number =>
  Math.pow(10, registerCurveDb(abs, s) / 20);

export function velocityFor(abs: number, s: Settings): number {
  const base = s.engine === 'sampler' ? SAMPLE_FLAT_VELOCITY : SYNTH_BASE_VELOCITY;
  return clamp(base * registerCurveGain(abs, s), 0.02, 1);
}

/**
 * Notes are tracked per *pitch*, with a set of the input sources holding each
 * one, rather than one entry per source. A synth voice belongs to a pitch: if
 * the same key is pressed with the mouse and the computer keyboard at once,
 * both map to one voice, so releasing either used to call triggerRelease and
 * silence the note while the other input was still held down. The voice is now
 * attacked when a pitch's holder set goes empty → non-empty and released only
 * when it goes back to empty.
 */
const holders = new Map<number, Set<string>>();
const pitchOf = new Map<string, number>();
/**
 * Pitches the sustain pedal is holding after every physical input let go. A
 * sustained note is tracked by *pitch*, not by the input that started it — a
 * real pedal keeps the string ringing no matter what the hand does next. The
 * previous code kept the note tied to its holder id, so the single mouse id,
 * reused on the next click, dropped the note it had just "sustained" and only
 * the newest note ever survived.
 */
const sustainedPitches = new Set<number>();

/** Number of distinct pitches currently sounding (played + pedal-held). */
export const heldCount = (): number => {
  let n = holders.size;
  for (const abs of sustainedPitches) if (!holders.has(abs)) n++;
  return n;
};
export const activePitches = (): readonly number[] => [...holders.keys()];
export const isSounding = (abs: number): boolean => holders.has(abs) || sustainedPitches.has(abs);

/** Sounding-voice count for the polyphony trim: played plus pedal-held pitches. */
function soundingCount(): number {
  return heldCount();
}

type PitchListener = (abs: number, active: boolean) => void;
let pitchListener: PitchListener | null = null;

/** Notified when a pitch starts or stops sounding, for the key highlighting. */
export function onPitchActive(fn: PitchListener): void {
  pitchListener = fn;
}

/**
 * Tone's timelines reject a start time that is not strictly after the previous
 * one for the same voice, and throw — killing the note. A PolySynth gives each
 * note name its own voice; the two monophonic synths have exactly one, so every
 * note there shares a single clock.
 */
const lastTime = new Map<string, number>();

function nextTime(noteName: string): number {
  const src = currentSource();
  const key = src && src.kind !== 'poly' ? ' mono' : noteName;
  const now = Tone.now();
  const prev = lastTime.get(key) ?? 0;
  const t = now > prev ? now : prev + 0.001;
  lastTime.set(key, t);
  return t;
}

let audioUnlocked = false;

/**
 * The AudioContext can only be resumed from a user gesture. Hooking this in the
 * capture phase means it runs before the key's own handler, so the very first
 * note is not swallowed.
 */
export function installAudioUnlock(): void {
  const unlock = (): void => {
    if (audioUnlocked) return;
    audioUnlocked = true;
    void Tone.start().catch(() => {
      audioUnlocked = false;
    });
  };
  for (const evt of ['mousedown', 'touchstart', 'keydown'] as const) {
    document.addEventListener(evt, unlock, { capture: true, passive: true });
  }
}

let chainWired = false;

/**
 * The graph leaves the filter → delay span for the effects section to close, so
 * rewire() has to run once even when no effect is enabled — otherwise the chain
 * is severed and nothing is audible. This is also the first moment the graph
 * exists, so restored settings are pushed into it here; the knob handlers that
 * normally do it run before any node has been created.
 */
export function ensureChain(s: Settings): void {
  ensureGraph();
  if (chainWired) return;
  chainWired = true;
  setFilterFrequency(s.filter);
  setEchoWet(s.echo);
  setReverbWet(s.reverb);
  setVolume(s.volume);
  rewire(s.effects);
}

export function ensureAudio(s: Settings): void {
  ensureChain(s);
  if (s.engine === 'sampler') {
    if (!isSamplerReady()) void ensureSampler(s.sampleInstrument, s.sampleDensity, s.attack, s.release);
  } else if (!currentSource()) {
    buildSynthSource(s);
  }
  if (Tone.getContext().state !== 'running') void Tone.start();
}

/** Switch engines / rebuild the active source after a settings change. */
export function rebuildSource(s: Settings): BuildResult {
  ensureChain(s);
  panic();
  if (s.engine === 'sampler') {
    disposeSource();
    setSourceTrim(sourceTrimFor(s));
    void ensureSampler(s.sampleInstrument, s.sampleDensity, s.attack, s.release);
    return {};
  }
  detachSampler();
  return buildSynthSource(s);
}

export function applyEnvelopeSettings(s: Settings): void {
  if (s.engine === 'sampler') setSamplerEnvelope(s.attack, s.release);
  else updateSourceOptions(s);
}

/** Sound the pitch. Returns false if nothing could be triggered. */
function attackPitch(abs: number, s: Settings): boolean {
  const noteName = toneName(abs);
  const velocity = velocityFor(abs, s);

  try {
    if (s.engine === 'sampler') {
      const sampler = currentSampler();
      if (!sampler) return false; // still loading — the status line explains why
      sampler.triggerAttack(noteName, nextTime(noteName), velocity);
      return true;
    }
    const src = currentSource();
    if (!src) return false;
    switch (src.kind) {
      case 'poly':
        src.node.triggerAttack(noteName, nextTime(noteName), velocity);
        return true;
      case 'noise':
        src.node.triggerAttack(nextTime(noteName), velocity);
        return true;
      case 'pluck':
        // Karplus-Strong: takes a pitch but no velocity.
        src.node.triggerAttack(noteName, nextTime(noteName));
        return true;
    }
  } catch (err) {
    console.warn('Attack eșuat:', err);
    return false;
  }
}

function releasePitch(abs: number, engine: Settings['engine']): void {
  const noteName = toneName(abs);
  try {
    if (engine === 'sampler') {
      currentSampler()?.triggerRelease(noteName, nextTime(noteName));
      return;
    }
    const src = currentSource();
    if (src?.kind === 'poly') src.node.triggerRelease(noteName, nextTime(noteName));
    else if (src) src.node.triggerRelease(nextTime(noteName));
  } catch (err) {
    console.warn('Release eșuat:', err);
  }
}

export function triggerNote(id: string, abs: number, s: Settings): void {
  ensureAudio(s);

  const previous = pitchOf.get(id);
  if (previous !== undefined && previous !== abs) dropHolder(id, s);
  if (pitchOf.get(id) === abs) return; // already held by this source

  let set = holders.get(abs);
  const first = set === undefined || set.size === 0;
  // Re-pressing a key the pedal is holding re-articulates it, like a piano.
  const reArticulate = sustainedPitches.delete(abs);

  if ((first || reArticulate) && !attackPitch(abs, s)) return;

  if (!set) {
    set = new Set();
    holders.set(abs, set);
  }
  set.add(id);
  pitchOf.set(id, abs);

  if (first) {
    pitchListener?.(abs, true);
    setActiveVoiceCount(soundingCount());
  }
}

/**
 * Remove one holder. When the last holder lets go, the pitch is released — or,
 * if the sustain pedal is down, kept ringing in `sustainedPitches` (its key
 * highlight clears, since no finger is on it) until the pedal lifts.
 */
function dropHolder(id: string, s: Settings): void {
  const abs = pitchOf.get(id);
  if (abs === undefined) return;
  pitchOf.delete(id);

  const set = holders.get(abs);
  if (!set) return;
  set.delete(id);
  if (set.size > 0) return;

  holders.delete(abs);

  if (s.sustain) {
    sustainedPitches.add(abs);
    pitchListener?.(abs, false); // finger up, but the note keeps sounding
    return;
  }

  releasePitch(abs, s.engine);
  pitchListener?.(abs, false);
  setActiveVoiceCount(soundingCount());
}

export function releaseNote(id: string, s: Settings): void {
  dropHolder(id, s);
}

/** Pedal up: stop every pitch the pedal was holding that no key is now pressing. */
export function releaseSustained(s: Settings): void {
  for (const abs of [...sustainedPitches]) {
    if (holders.has(abs)) continue; // pressed again in the meantime
    releasePitch(abs, s.engine);
    pitchListener?.(abs, false);
  }
  sustainedPitches.clear();
  setActiveVoiceCount(soundingCount());
}

/** Stop everything: engine switch, window blur, reset. */
export function panic(s?: Settings): void {
  const engine = s?.engine ?? 'synth';
  const sounding = new Set<number>([...holders.keys(), ...sustainedPitches]);
  for (const abs of sounding) {
    releasePitch(abs, engine);
    pitchListener?.(abs, false);
  }
  holders.clear();
  pitchOf.clear();
  sustainedPitches.clear();
  lastTime.clear();
  setActiveVoiceCount(0);
}
